// Consulta rápida "en la calle" (Tarea 19B, Req. 11.1, 11.3–11.7, 11.10, 11.12, 11.14).
//
// Entrada por texto / número de catálogo / foto (cámara). Resultado combinado que
// DESTACA si está en la wishlist (y si ya está en el catálogo), muestra el
// análisis de IA (ranking del álbum + top 10 canciones presentes) y los precios
// (nuevo + 3 mejores usados con moneda). Botón para agregar a la wishlist.
// Optimizado para móvil y baja conexión (reintentos del cliente).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useApi } from '../api/ApiContext.js';
import { AlertMessage, Badge, ButtonSpinner, Card, Icon, PageHeader } from '../ui/components.js';
import type { QuickLookupResult } from '../types.js';
import { useAudioClip } from './useAudioClip.js';

type InputType = 'text' | 'catalog_nr' | 'photo' | 'sound';

/** Identificado del resultado (para tipar los sub-componentes). */
type Identified = NonNullable<QuickLookupResult['identified']>;

export function QuickLookupView() {
  const { t } = useTranslation();
  const api = useApi();
  const [inputType, setInputType] = useState<InputType>('text');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<QuickLookupResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState(false);
  // Modo Sonido: reconocimiento por micrófono (tipo Shazam). Lo reconocido se
  // re-alimenta a la consulta rápida por artista+título para obtener el resultado
  // completo (precios, análisis IA) idéntico a las otras entradas. Se graba hasta
  // 15s y se envía la mejor ventana de ~5.6s (límite de Shazam).
  const clip = useAudioClip(15000);
  const [soundNote, setSoundNote] = useState<string | null>(null);

  /** Selecciona (o limpia) la foto y gestiona la URL de vista previa. */
  function pickFile(f: File | null) {
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return f ? URL.createObjectURL(f) : null;
    });
    setFile(f);
  }

  // Libera la URL de vista previa al desmontar la vista (evita fugas de memoria).
  useEffect(() => () => setPreview((p) => (p ? (URL.revokeObjectURL(p), null) : null)), []);

  async function run() {
    // El modo Sonido tiene su propio flujo (grabar → reconocer → consultar).
    if (inputType === 'sound') {
      void runSound();
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    setAdded(false);
    try {
      let s3Key: string | undefined;
      if (inputType === 'photo' && file) {
        const { upload_url, s3_key } = await api.ingestion.createUpload(
          file.type || 'image/jpeg',
          file.name,
        );
        await fetch(upload_url, {
          method: 'PUT',
          headers: { 'content-type': file.type || 'image/jpeg' },
          body: file,
        });
        s3Key = s3_key;
      }
      const res = await api.quickLookup.run({
        input_type: inputType,
        text: inputType === 'photo' ? undefined : text,
        s3_key: s3Key,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('quickLookup.errLookup'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Modo Sonido (Req. 12): graba un clip, lo reconoce (tipo Shazam) y re-alimenta
   * el artista+título reconocidos a la consulta rápida por texto, para obtener el
   * resultado COMPLETO (identificación, análisis de IA y precios) igual que las
   * otras entradas. Si no reconoce, informa el motivo y no consulta.
   */
  async function runSound() {
    setError(null);
    setResult(null);
    setAdded(false);
    setSoundNote(null);
    const base64 = await clip.start();
    if (!base64) {
      setError(clip.error ?? t('quickLookup.errAudioPrepare'));
      return;
    }
    setBusy(true);
    try {
      const audio = await api.audio.recognize({ audio_base64: base64 });
      const artist = audio.identified?.artist ?? audio.recognized_artist;
      const title = audio.identified?.title ?? audio.recognized_album ?? audio.recognized_track;
      if (!audio.matched || !artist || !title) {
        setSoundNote(soundNotMatchedMessage(t, audio.reason));
        return;
      }
      setSoundNote(t('quickLookup.recognized', { artist, title }));
      const res = await api.quickLookup.run({ input_type: 'text', artist, title });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('quickLookup.errRecognize'));
    } finally {
      setBusy(false);
    }
  }

  async function addToWishlist() {
    if (!result) return;
    try {
      await api.quickLookup.addToWishlist(result.lookup_id);
      setAdded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('quickLookup.errAddWishlist'));
    }
  }

  /** Cambia de pestaña de entrada y LIMPIA lo ingresado previamente (texto,
   *  foto y resultado) para no arrastrar datos de otra búsqueda. */
  function switchInput(next: InputType) {
    if (next === inputType) return;
    setInputType(next);
    setText('');
    pickFile(null);
    setResult(null);
    setError(null);
    setAdded(false);
    setSoundNote(null);
  }

  const inputs: { id: InputType; label: string; icon: string }[] = [
    { id: 'text', label: t('quickLookup.inputText'), icon: 'title' },
    { id: 'catalog_nr', label: t('quickLookup.inputCatalog'), icon: 'tag' },
    { id: 'photo', label: t('quickLookup.inputPhoto'), icon: 'photo_camera' },
    { id: 'sound', label: t('quickLookup.inputSound'), icon: 'graphic_eq' },
  ];

  return (
    <div className="flex flex-col">
      <PageHeader
        eyebrow={t('quickLookup.eyebrow')}
        title={t('quickLookup.pageTitle')}
        subtitle={t('quickLookup.pageSubtitle')}
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <AlertMessage message={error} />

        <div className="surface-card flex flex-col gap-space-sm">
          {/* Selector de entrada */}
          <div
            data-tour="lookup-input-modes"
            className="flex items-center gap-space-xs p-1 rounded-lg bg-surface-deck border border-border-subtle"
          >
            {inputs.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => switchInput(it.id)}
                className={`flex-1 h-10 rounded flex items-center justify-center gap-space-xs font-body-sm text-body-sm transition-colors ${
                  inputType === it.id ? 'bg-surface-rack text-primary' : 'text-on-surface-variant'
                }`}
              >
                <Icon name={it.icon} className="text-[16px]" /> {it.label}
              </button>
            ))}
          </div>

          {inputType === 'sound' ? (
            <SoundInputPanel
              t={t}
              state={clip.state}
              busy={busy}
              note={soundNote}
              onStart={() => void runSound()}
              onStop={() => clip.stop()}
            />
          ) : inputType === 'photo' ? (
            preview ? (
              <div className="relative h-40 rounded-lg border border-border-subtle bg-surface-deck overflow-hidden">
                <img
                  src={preview}
                  alt={t('quickLookup.previewAlt')}
                  className="w-full h-full object-contain"
                />
                {/* Acciones sobre la imagen: reemplazar o quitar. */}
                <div className="absolute top-space-xs right-space-xs flex items-center gap-space-xs">
                  <label
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-surface-deck/90 backdrop-blur border border-border-subtle text-on-surface font-body-sm text-body-sm cursor-pointer active:scale-95 transition-transform"
                    aria-label={t('quickLookup.replaceAria')}
                  >
                    <Icon name="swap_horiz" className="text-[16px]" /> {t('quickLookup.replace')}
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      disabled={busy}
                      onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                    />
                  </label>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-surface-deck/90 backdrop-blur border border-error/40 text-error font-body-sm text-body-sm active:scale-95 transition-transform disabled:opacity-50"
                    disabled={busy}
                    onClick={() => pickFile(null)}
                    aria-label={t('quickLookup.removeImageAria')}
                  >
                    <Icon name="delete" className="text-[16px]" /> {t('quickLookup.removeImage')}
                  </button>
                </div>
              </div>
            ) : (
              <label className="field-slot h-24 flex-col justify-center cursor-pointer border-dashed">
                <Icon name="add_a_photo" className="text-primary text-[28px]" />
                <span className="font-body-sm text-body-sm text-on-surface-variant mt-1">
                  {t('quickLookup.captureCover')}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                />
              </label>
            )
          ) : (
            <div className="field-slot h-12">
              <Icon name="search" className="text-on-surface-variant text-[18px] mr-space-xs" />
              <input
                className="field-input"
                placeholder={
                  inputType === 'text'
                    ? t('quickLookup.textPlaceholder')
                    : t('quickLookup.catalogPlaceholder')
                }
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
          )}
          {inputType !== 'sound' && (
            <button
              className="btn-primary w-full"
              type="button"
              data-tour="lookup-run"
              disabled={busy || (inputType === 'photo' ? !file : !text)}
              onClick={() => void run()}
            >
              {busy ? <ButtonSpinner /> : <Icon name="radar" className="text-[18px]" />}
              {busy ? t('quickLookup.querying') : t('quickLookup.queryRadar')}
            </button>
          )}
        </div>

        {result && (
          <Card title={t('quickLookup.resultTitle')} icon="album">
            <div className="flex items-center gap-space-xs mb-space-sm">
              {result.in_wishlist && <Badge tone="warn">{t('quickLookup.inWishlist')}</Badge>}
              {result.in_catalog && <Badge tone="success">{t('quickLookup.inCatalog')}</Badge>}
              {!result.in_wishlist && !result.in_catalog && (
                <Badge tone="info">{t('quickLookup.newForYou')}</Badge>
              )}
            </div>

            {(() => {
              // Lista de candidatas ordenadas por exactitud (el backend ya las
              // ordena de mayor a menor). Compatibilidad: si no viene `candidates`,
              // se usa `identified` como única candidata.
              const candidates =
                result.candidates && result.candidates.length > 0
                  ? result.candidates
                  : result.identified
                    ? [result.identified]
                    : [];
              if (candidates.length === 0) {
                return (
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    {t('quickLookup.noConfidentRelease')}
                  </p>
                );
              }
              // Garantiza el orden de mayor a menor exactitud (defensivo: el backend
              // ya las ordena, pero no dependemos de ello en la UI).
              const ordered = [...candidates].sort((a, b) => b.score - a.score);
              return (
                <div className="flex flex-col gap-space-md">
                  {ordered.length > 1 && (
                    <span className="section-label text-vacuum-glow">
                      {t('quickLookup.topMatches', { count: ordered.length })}
                    </span>
                  )}
                  {/* Todas las candidatas con el MISMO nivel de detalle, una debajo
                    de otra, de mayor a menor exactitud. */}
                  {ordered.map((c, i) => (
                    <CandidateFull key={`${c.discogsReleaseId ?? 'c'}-${i}`} candidate={c} />
                  ))}
                </div>
              );
            })()}

            {result.ai_analysis && (
              <div className="mt-space-md">
                {result.ai_analysis.available ? (
                  <>
                    {/* Top álbumes del artista (mejor a menor), con el ranking del
                      álbum consultado dentro de la discografía. */}
                    {(result.ai_analysis.topAlbums.length > 0 ||
                      result.ai_analysis.albumRank != null) && (
                      <div>
                        <span className="section-label text-vacuum-glow">
                          {t('quickLookup.topAlbums')}
                        </span>
                        {result.ai_analysis.albumRank != null && (
                          <p className="font-body-sm text-body-sm text-on-surface mt-space-xs">
                            {result.ai_analysis.albumsConsidered
                              ? t('quickLookup.albumRankOf', {
                                  rank: result.ai_analysis.albumRank,
                                  total: result.ai_analysis.albumsConsidered,
                                })
                              : t('quickLookup.albumRank', { rank: result.ai_analysis.albumRank })}
                          </p>
                        )}
                        <ol className="flex flex-col gap-1 mt-space-xs">
                          {result.ai_analysis.topAlbums.map((a) => (
                            <li
                              key={a.rank}
                              className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface"
                            >
                              <span className="font-code-catalog text-primary">{a.rank}.</span>{' '}
                              {a.title}
                              {a.year ? (
                                <span className="text-on-surface-variant">({a.year})</span>
                              ) : null}
                              {a.isCurrent && (
                                <Badge tone="success">{t('quickLookup.thisRecord')}</Badge>
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Top canciones del artista */}
                    <div className="mt-space-md">
                      <span className="section-label text-vacuum-glow">
                        {t('quickLookup.topSongs')}
                      </span>
                      <ol className="flex flex-col gap-1 mt-space-xs">
                        {result.ai_analysis.topSongs.map((s) => (
                          <li
                            key={s.rank}
                            className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface"
                          >
                            <span className="font-code-catalog text-primary">{s.rank}.</span>{' '}
                            {s.title}
                            {s.inDisc && <Badge tone="success">{t('quickLookup.inDisc')}</Badge>}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </>
                ) : (
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-space-xs">
                    {t('quickLookup.analysisUnavailable')} {result.ai_analysis.error ?? ''}
                  </p>
                )}
              </div>
            )}

            {result.prices && (
              <div className="mt-space-md">
                <span className="section-label text-vacuum-glow">{t('quickLookup.prices')}</span>
                {result.prices.available ? (
                  <ul className="flex flex-col gap-space-xs mt-space-xs">
                    {result.prices.new && (
                      <li className="flex items-center justify-between surface-rack-card !p-space-sm">
                        <span className="font-body-sm text-body-sm text-on-surface">
                          {t('quickLookup.priceNew', { label: result.prices.new.label })}
                        </span>
                        <span className="font-code-catalog text-body-sm text-goldmine-mint">
                          {price(t, result.prices.new)}
                        </span>
                      </li>
                    )}
                    {result.prices.usedTop3.map((p) => (
                      <li
                        key={p.condition}
                        className="flex items-center justify-between surface-rack-card !p-space-sm"
                      >
                        <span className="font-body-sm text-body-sm text-on-surface">{p.label}</span>
                        <span className="font-code-catalog text-body-sm text-primary">
                          {price(t, p)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-space-xs">
                    {t('quickLookup.noPriceData')}
                  </p>
                )}
                {result.prices.degraded && (
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-space-xs">
                    {t('quickLookup.pricesApprox')}
                  </p>
                )}
              </div>
            )}

            {/* Si el disco YA está en el catálogo no tiene sentido ofrecer
              agregarlo a la wishlist (la wishlist es de lo que aún no se tiene). */}
            {!result.in_catalog && (
              <button
                className="btn-primary w-full mt-space-md"
                type="button"
                disabled={added}
                onClick={() => void addToWishlist()}
              >
                <Icon name={added ? 'check' : 'favorite'} className="text-[18px]" />
                {added ? t('quickLookup.added') : t('quickLookup.addToWishlist')}
              </button>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

function price(t: TFunction, p: { value: number | null; currency: string | null }): string {
  if (p.value == null) return t('quickLookup.noData');
  return `${p.value.toFixed(2)} ${p.currency ?? ''}`.trim();
}

/**
 * Panel de la entrada por Sonido (Req. 12): botón de radar que graba ~7s y
 * reconoce la canción (tipo Shazam). Lo reconocido se re-alimenta a la consulta
 * rápida por artista+título (en el contenedor), por lo que el resultado se
 * muestra con la misma tarjeta que las otras entradas. `note` informa el track
 * reconocido o el motivo si no se identificó.
 */
function SoundInputPanel({
  t,
  state,
  busy,
  note,
  onStart,
  onStop,
}: {
  t: TFunction;
  state: 'idle' | 'recording' | 'processing';
  busy: boolean;
  note: string | null;
  onStart: () => void;
  onStop: () => void;
}) {
  const working = state === 'recording' || state === 'processing' || busy;
  const label =
    state === 'recording'
      ? t('quickLookup.soundListening')
      : working
        ? t('quickLookup.soundRecognizing')
        : t('quickLookup.soundIdle');

  return (
    <div className="flex flex-col items-center gap-space-sm py-space-xs">
      <button
        type="button"
        onClick={() =>
          state === 'recording' ? onStop() : state === 'idle' && !busy ? onStart() : undefined
        }
        disabled={state === 'processing' || busy}
        aria-label={
          state === 'recording' ? t('quickLookup.soundStop') : t('quickLookup.soundListen')
        }
        className="relative w-28 h-28 rounded-full flex items-center justify-center focus:outline-none"
      >
        <span
          className={`absolute inset-0 rounded-full border ${
            state === 'recording' ? 'border-priority-urgent animate-ping' : 'border-border-subtle'
          }`}
        />
        <span
          className={`w-20 h-20 rounded-full flex items-center justify-center shadow-[0_0_24px_rgba(56,189,248,0.35)] ${
            state === 'recording'
              ? 'bg-priority-urgent text-white'
              : 'bg-primary-container text-on-primary'
          }`}
        >
          <Icon
            name={state === 'recording' ? 'stop' : working ? 'graphic_eq' : 'mic'}
            className="text-[32px]"
          />
        </span>
      </button>
      <p className="font-body-sm text-body-sm text-on-surface-variant text-center max-w-xs">
        {label}
      </p>
      {note && !working && (
        <p className="font-body-sm text-body-sm text-on-surface text-center">{note}</p>
      )}
    </div>
  );
}

/** Mensaje breve cuando el reconocimiento por sonido no identifica la canción. */
function soundNotMatchedMessage(
  t: TFunction,
  reason: 'matched' | 'no_match' | 'quota_exceeded' | 'provider_error',
): string {
  switch (reason) {
    case 'quota_exceeded':
      return t('quickLookup.soundReasonQuota');
    case 'provider_error':
      return t('quickLookup.soundReasonProvider');
    case 'no_match':
    default:
      return t('quickLookup.soundReasonNoMatch');
  }
}

/**
 * Tarjeta de una coincidencia con el MISMO nivel de detalle para todas: carátula,
 * artista/título/año, barra de exactitud, pistas, enlace a Discogs y los
 * metadatos completos (formato, sello, nº de catálogo, origen, género, estilos).
 * Se renderiza una por candidata, ordenadas de mayor a menor exactitud.
 */
function CandidateFull({ candidate: c }: { candidate: Identified }) {
  const { t } = useTranslation();
  return (
    <div className="surface-rack-card !p-space-sm flex flex-col gap-space-sm">
      <div className="flex gap-space-sm">
        {/* Carátula para confirmar visualmente que es el disco correcto. */}
        <div className="w-20 h-20 shrink-0 rounded-lg bg-surface-container-high border border-border-subtle overflow-hidden flex items-center justify-center">
          {c.coverImageUrl ? (
            <img
              src={c.coverImageUrl}
              alt={t('quickLookup.coverAlt', { title: c.title ?? '' })}
              className="w-full h-full object-cover"
              onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
            />
          ) : (
            <Icon name="album" className="text-on-surface-variant text-[28px] opacity-50" />
          )}
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <p className="font-body-md text-body-md text-on-surface">
            <span className="font-bold">{c.artist ?? '—'}</span> — {c.title ?? '—'}{' '}
            {c.year ? `(${c.year})` : ''}
          </p>
          {/* Exactitud del match: ayuda a decidir si es el disco correcto. */}
          <div className="flex items-center gap-space-xs">
            <div className="meter-track flex-1 max-w-[160px]">
              <div
                className="h-full bg-gradient-to-r from-goldmine-vg to-primary-container"
                style={{ width: `${Math.round(c.score)}%` }}
              />
            </div>
            <span className="font-code-catalog text-body-sm text-primary font-bold">
              {t('quickLookup.exactness', { score: Math.round(c.score) })}
            </span>
          </div>
          {c.discogsReleaseId != null && (
            <a
              href={`https://www.discogs.com/release/${c.discogsReleaseId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body-sm text-body-sm text-primary inline-flex items-center gap-1"
            >
              <Icon name="open_in_new" className="text-[14px]" /> {t('quickLookup.viewOnDiscogs')}
            </a>
          )}
        </div>
      </div>
      <ReleaseMeta identified={c} />
      {/* Tracklist completo dentro de la tarjeta, en 2 columnas para ocupar
          menos alto. `columns-2` reparte las pistas de forma fluida; cada ítem
          evita cortarse entre columnas (break-inside-avoid). */}
      {c.trackTitles.length > 0 && (
        <div className="flex flex-col gap-space-xs">
          <span className="section-label text-vacuum-glow">
            {t('quickLookup.tracks', { count: c.trackTitles.length })}
          </span>
          <ol className="columns-1 sm:columns-2 gap-space-md">
            {c.trackTitles.map((track, i) => (
              <li
                key={i}
                className="flex gap-space-xs font-body-sm text-body-sm text-on-surface break-inside-avoid mb-0.5"
              >
                <span className="font-code-catalog text-on-surface-variant shrink-0 w-6 text-right">
                  {i + 1}.
                </span>
                <span className="min-w-0">{track}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

/**
 * Metadatos del release identificado (año, género, estilos, formato/tipo, sello,
 * nº de catálogo, país) en forma de chips, para dar contexto del disco.
 */
function ReleaseMeta({ identified: id }: { identified: Identified }) {
  const { t } = useTranslation();
  const chips: { icon: string; label: string; value: string }[] = [];
  if (id.year)
    chips.push({
      icon: 'calendar_today',
      label: t('quickLookup.metaYear'),
      value: String(id.year),
    });
  if (id.genres.length)
    chips.push({
      icon: 'music_note',
      label: t('quickLookup.metaGenre'),
      value: id.genres.join(', '),
    });
  if (id.styles.length)
    chips.push({
      icon: 'graphic_eq',
      label: t('quickLookup.metaStyles'),
      value: id.styles.join(', '),
    });
  if (id.formats.length)
    chips.push({ icon: 'album', label: t('quickLookup.metaFormat'), value: id.formats.join(', ') });
  if (id.label) chips.push({ icon: 'label', label: t('quickLookup.metaLabel'), value: id.label });
  if (id.catalogNumber)
    chips.push({ icon: 'tag', label: t('quickLookup.metaCatalog'), value: id.catalogNumber });
  if (id.country)
    chips.push({ icon: 'public', label: t('quickLookup.metaOrigin'), value: id.country });
  if (chips.length === 0) return null;
  return (
    <div className="mt-space-md flex flex-wrap gap-space-xs">
      {chips.map((c) => (
        <span
          key={c.label}
          className="flex items-center gap-1 px-2 py-1 rounded bg-surface-rack border border-border-subtle font-body-sm text-body-sm text-on-surface"
        >
          <Icon name={c.icon} className="text-[14px] text-primary" />
          <span className="text-on-surface-variant">{c.label}:</span> {c.value}
        </span>
      ))}
    </div>
  );
}
